/**
 * Cognito Authentication Service
 * Wraps AWS Amplify Auth functions to provide a clean interface for authentication operations
 */

import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword as amplifyResetPassword,
  confirmResetPassword,
  updatePassword,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchUserAttributes,
  fetchAuthSession,
  updateUserAttributes,
  updateUserAttribute,
  confirmUserAttribute,
  sendUserAttributeVerificationCode,
  confirmSignIn,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
} from 'aws-amplify/auth';
import type { SignInOutput } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';

import type {
  AuthenticatorSetup,
  HandledSignInStep,
  IAuthService,
  SignInCredentials,
  SignUpData,
  VerifyEmailData,
  ForgotPasswordData,
  ResetPasswordData,
  ChangePasswordData,
  CognitoSignInResult,
  CognitoSignUpResult,
  CognitoSession,
  CognitoUserAttributes,
  AuthError,
  EmailChangeStep,
} from '@/types/auth';

const HANDLED_SIGN_IN_STEPS: readonly string[] = [
  'DONE',
  'CONFIRM_SIGN_UP',
  'RESET_PASSWORD',
  'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
] satisfies HandledSignInStep[];

/** The name customers see beside the code in their authenticator app. */
const AUTHENTICATOR_ISSUER = 'socialyse.ai';

/** Wording for an authenticator code that was wrong or arrived too late. */
const CODE_MESSAGES: Partial<Record<AuthError['code'], string>> = {
  CodeMismatchException:
    "That code didn't match. Check the app shows socialyse.ai, then try the newest code.",
  // #593, seen live on dev: Cognito accepts each code once ("Your software
  // token has already been used once."), so typing the same code again asks
  // for the next one — not to "request a new one", which nobody sends.
  ExpiredCodeException: 'That code has already been used. Wait for the next code in your app, then enter it.',
  // At the sign-in code step this means the sign-in attempt itself expired
  // (Cognito allows a few minutes), not a wrong password.
  NotAuthorizedException: 'Your sign-in timed out. Please start again.',
};

/** Wording for the password check before two-step sign-in is turned off (#593). */
const CANT_CHECK_PASSWORD = "We couldn't check your password just now. Please try again in a moment.";
const PASSWORD_CHECK_MESSAGES: Partial<Record<AuthError['code'], string>> = {
  NotAuthorizedException: "That password isn't right.",
  LimitExceededException: 'Too many tries. Wait a few minutes and try again.',
  InvalidParameterException: CANT_CHECK_PASSWORD,
  UnknownError: CANT_CHECK_PASSWORD,
};

/**
 * ...and for the switch-off itself, once the password has passed. A failure
 * here is never the password's fault: NotAuthorized means the session ended.
 */
const SWITCH_OFF_MESSAGES: Partial<Record<AuthError['code'], string>> = {
  NotAuthorizedException: 'Your sign-in has ended. Please sign in again, then try once more.',
  UnknownError: "We couldn't turn it off just now. Please try again in a moment.",
};

/**
 * Wording for the email-change calls (#222). Cognito exceptions we have no
 * copy for (CodeDeliveryFailureException, TooManyRequestsException, ...) would
 * otherwise show Cognito's own text; this keeps them in our words.
 */
const EMAIL_CHANGE_MESSAGES: Partial<Record<AuthError['code'], string>> = {
  UnknownError: "We couldn't change your email just now. Please try again in a moment.",
};

/** ...and for the code we email to the NEW address. */
const EMAIL_CODE_MESSAGES: Partial<Record<AuthError['code'], string>> = {
  ...EMAIL_CHANGE_MESSAGES,
  CodeMismatchException:
    "That code didn't match. Check the newest email we sent to your new address.",
  ExpiredCodeException: 'That code has expired. Send a new code and try again.',
};

/**
 * #222 — the cognito-hooks Lambda refuses an address by raising one of these
 * tokens. Cognito wraps it in its own words ("PreSignUp failed with error
 * EMAIL_THROWAWAY."), so match on the token, never the whole sentence.
 */
const HOOK_REFUSALS: ReadonlyArray<[token: string, code: AuthError['code']]> = [
  ['EMAIL_THROWAWAY', 'EmailThrowaway'],
  ['EMAIL_UNREACHABLE', 'EmailUnreachable'],
];

/** Cognito's names for a trigger that broke or answered badly: ours to fix. */
const TRIGGER_FAULTS = [
  'UserLambdaValidationException',
  'UnexpectedLambdaException',
  'InvalidLambdaResponseException',
];

/**
 * Cognito Authentication Service Implementation
 */
class CognitoAuthService implements IAuthService {
  /**
   * Sign in user with email and password
   */
  async signIn(credentials: SignInCredentials): Promise<CognitoSignInResult> {
    try {
      // Clear any stale Amplify session before signing in.
      // Amplify v6 throws UserAlreadyAuthenticatedException if a session
      // exists in localStorage when signIn() is called.
      try {
        await amplifySignOut();
      } catch {
        // No existing session — safe to continue.
      }

      const result = await amplifySignIn({
        username: credentials.email,
        password: credentials.password,
      });

      return this.toSignInResult(result);
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Answer the authenticator-code step of sign-in (#220)
   */
  async confirmSignInCode(code: string): Promise<CognitoSignInResult> {
    try {
      return this.toSignInResult(await confirmSignIn({ challengeResponse: code }));
    } catch (error) {
      throw this.handleAuthError(error, CODE_MESSAGES);
    }
  }

  private async toSignInResult(result: SignInOutput): Promise<CognitoSignInResult> {
    const step = result.nextStep.signInStep;
    const handled = HANDLED_SIGN_IN_STEPS.includes(step);
    if (!handled) {
      console.error('[CognitoAuth] Sign-in step this app cannot complete:', step);
    }

    return {
      isSignedIn: result.isSignedIn,
      nextStep: {
        signInStep: handled ? (step as HandledSignInStep) : 'UNSUPPORTED',
      },
      userId: result.isSignedIn ? (await this.getCurrentUser())?.sub : undefined,
    };
  }

  /**
   * Sign up new user with email, password, and name
   */
  async signUp(data: SignUpData): Promise<CognitoSignUpResult> {
    try {
      const result = await amplifySignUp({
        username: data.email,
        password: data.password,
        options: {
          userAttributes: {
            email: data.email,
            name: data.name,
          },
        },
      });

      return {
        isSignUpComplete: result.isSignUpComplete,
        nextStep: {
          signUpStep: result.nextStep.signUpStep as 'CONFIRM_SIGN_UP' | 'DONE',
        },
        userId: result.userId,
      };
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Verify email address with confirmation code
   */
  async verifyEmail(data: VerifyEmailData): Promise<void> {
    try {
      await confirmSignUp({
        username: data.email,
        confirmationCode: data.code,
      });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Resend email verification code
   */
  async resendVerificationCode(email: string): Promise<void> {
    try {
      await resendSignUpCode({
        username: email,
      });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Initiate forgot password flow (sends reset code to email)
   */
  async forgotPassword(data: ForgotPasswordData): Promise<void> {
    try {
      await amplifyResetPassword({
        username: data.email,
      });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Reset password with confirmation code
   */
  async resetPassword(data: ResetPasswordData): Promise<void> {
    try {
      await confirmResetPassword({
        username: data.email,
        confirmationCode: data.code,
        newPassword: data.newPassword,
      });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Change password for authenticated user
   */
  async changePassword(data: ChangePasswordData): Promise<void> {
    try {
      await updatePassword({
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Whether the signed-in user has an authenticator app switched on
   */
  async isAuthenticatorOn(): Promise<boolean> {
    try {
      const { enabled } = await fetchMFAPreference();
      return enabled?.includes('TOTP') ?? false;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Start adding socialyse.ai to the user's authenticator app. Nothing changes
   * on the account until confirmAuthenticatorSetup succeeds.
   */
  async startAuthenticatorSetup(accountName: string): Promise<AuthenticatorSetup> {
    try {
      const details = await setUpTOTP();
      return {
        setupKey: details.sharedSecret,
        setupUri: details.getSetupUri(AUTHENTICATOR_ISSUER, accountName).toString(),
      };
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Prove the app works with one code, then make it required at sign-in
   */
  async confirmAuthenticatorSetup(code: string): Promise<void> {
    try {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: 'PREFERRED' });
    } catch (error) {
      throw this.handleAuthError(error, CODE_MESSAGES);
    }
  }

  /**
   * Stop asking the signed-in user for an authenticator code, but only once
   * their password has been checked again (#593), as AWS's guide for removing
   * an authenticator advises. A wrong password changes nothing.
   *
   * The password alone is enough: a customer who has lost their phone but is
   * still signed in can switch it off themselves instead of writing to support.
   *
   * This is a check in the website, not a rule Cognito enforces: Cognito
   * accepts SetUserMFAPreference on the session's access token alone. So it
   * stops someone using our page at a computer left signed in; it does not
   * stop someone who has stolen the session's tokens.
   */
  async turnOffAuthenticator(password: string): Promise<void> {
    try {
      await this.checkPassword(password);
    } catch (error) {
      throw this.handleAuthError(error, PASSWORD_CHECK_MESSAGES);
    }
    try {
      await updateMFAPreference({ totp: 'DISABLED' });
    } catch (error) {
      throw this.handleAuthError(error, SWITCH_OFF_MESSAGES);
    }
  }

  /**
   * Check the signed-in user's password without touching their session (#593).
   *
   * Amplify refuses to sign in while a session exists, so this calls Cognito's
   * InitiateAuth itself with USER_PASSWORD_AUTH, which the app client allows
   * only where two-step sign-in is switched on (06-api). For an account with
   * the authenticator on, a right password is answered with the code challenge
   * and a wrong one with NotAuthorizedException. The challenge is the proof and
   * is left unanswered, so no second session is created. (Were Cognito to
   * return tokens instead, because the account had no code switched on, they
   * are dropped here unused and never stored.)
   */
  private async checkPassword(password: string): Promise<void> {
    const { userPoolId = '', userPoolClientId = '' } = Amplify.getConfig().Auth?.Cognito ?? {};
    // A pool id is "<region>_<id>"; Amplify finds the region the same way.
    const region = userPoolId.split('_')[0];
    // The pool's own username for this account, which an email change
    // (#222) does not alter.
    const { username } = await getCurrentUser();

    let response: Response;
    try {
      response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
        },
        body: JSON.stringify({
          AuthFlow: 'USER_PASSWORD_AUTH',
          ClientId: userPoolClientId,
          AuthParameters: { USERNAME: username, PASSWORD: password },
        }),
      });
    } catch {
      throw { name: 'NetworkError', message: '' };
    }
    if (response.ok) return;

    const body = (await response.json().catch(() => ({}))) as { __type?: string; message?: string };
    const message = body.message ?? '';
    // Cognito reports too many wrong passwords as NotAuthorizedException too.
    if (/attempts exceeded/i.test(message)) {
      throw { name: 'LimitExceededException', message };
    }
    // __type is "NotAuthorizedException", or prefixed with a namespace and "#".
    throw { name: body.__type?.split('#').pop() || 'UnknownError', message };
  }

  /**
   * Ask to move the account to a new email address (#222). Cognito emails a
   * code to the new address and keeps the current one working until that code
   * is entered (UserAttributeUpdateSettings in 06-api). Our cognito-hooks
   * trigger refuses a throwaway new address and warns the current one.
   */
  async startEmailChange(newEmail: string): Promise<EmailChangeStep> {
    try {
      const value = newEmail.trim();
      const result = await updateUserAttribute({
        userAttribute: { attributeKey: 'email', value },
        // Cognito's trigger event carries only the CURRENT address, so the
        // address being requested travels here for cognito-hooks to check.
        options: { clientMetadata: { newEmail: value } },
      });
      return result.nextStep.updateAttributeStep === 'CONFIRM_ATTRIBUTE_WITH_CODE' ? 'CONFIRM' : 'DONE';
    } catch (error) {
      throw this.handleAuthError(error, EMAIL_CHANGE_MESSAGES);
    }
  }

  /**
   * Enter the code sent to the new address. Only now does the account's email
   * change. The session is refreshed so the next request carries the new
   * address (checkout reads it from the token for the receipt).
   */
  async confirmEmailChange(code: string): Promise<void> {
    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code });
    } catch (error) {
      throw this.handleAuthError(error, EMAIL_CODE_MESSAGES);
    }
    try {
      await fetchAuthSession({ forceRefresh: true });
    } catch (error) {
      // The change has happened; a stale token refreshes itself within the hour.
      console.error('[CognitoAuth] Session refresh after email change failed:', error);
    }
  }

  /** Send a new code to the address being changed to (checked again by cognito-hooks). */
  async resendEmailChangeCode(newEmail: string): Promise<void> {
    try {
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'email',
        options: { clientMetadata: { newEmail: newEmail.trim() } },
      });
    } catch (error) {
      throw this.handleAuthError(error, EMAIL_CHANGE_MESSAGES);
    }
  }

  /**
   * Get current authenticated session with tokens
   */
  async getCurrentSession(): Promise<CognitoSession | null> {
    try {
      const session = await fetchAuthSession();

      if (!session.tokens) {
        return null;
      }

      return {
        tokens: {
          idToken: session.tokens.idToken?.toString() || '',
          accessToken: session.tokens.accessToken?.toString() || '',
          refreshToken: (session.tokens as unknown as Record<string, unknown>).refreshToken?.toString(),
        },
        userSub: session.tokens.idToken?.payload.sub as string,
      };
    } catch (error) {
      console.error('[CognitoAuth] Failed to get session:', error);
      return null;
    }
  }

  /**
   * Get current authenticated user attributes
   */
  async getCurrentUser(): Promise<CognitoUserAttributes | null> {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();

      return {
        email: attributes.email || '',
        email_verified: attributes.email_verified === 'true',
        name: attributes.name,
        sub: user.userId,
      };
    } catch (error) {
      console.error('[CognitoAuth] Failed to get current user:', error);
      return null;
    }
  }

  /**
   * Update the authenticated user's display name in Cognito
   */
  async updateName(name: string): Promise<void> {
    try {
      await updateUserAttributes({ userAttributes: { name } });
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      await amplifySignOut();
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Fetch user attributes (alias for getCurrentUser for interface compatibility)
   */
  async fetchUserAttributes(): Promise<CognitoUserAttributes> {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    return user;
  }

  /**
   * Handle and normalize Cognito errors
   */
  private handleAuthError(
    error: unknown,
    overrides: Partial<Record<AuthError['code'], string>> = {}
  ): AuthError {
    const err = error as { name?: string; message?: string };

    // Map Cognito error names to our AuthErrorCode type
    const errorCode = this.mapErrorCode(err.name || 'UnknownError', err.message || '');

    return {
      code: errorCode,
      message: overrides[errorCode] ?? this.getUserFriendlyMessage(errorCode, err.message),
      name: err.name || 'UnknownError',
    };
  }

  /**
   * Map Cognito error names to AuthErrorCode
   */
  private mapErrorCode(cognitoErrorName: string, message = '') {
    // #222 — a deliberate refusal by our own trigger, carried in the message.
    if (TRIGGER_FAULTS.includes(cognitoErrorName)) {
      const refusal = HOOK_REFUSALS.find(([token]) => message.includes(token));
      return refusal ? refusal[1] : 'ServiceError';
    }

    // Staff paused this account (Backstage "Suspend"): Cognito says the user is disabled.
    if (cognitoErrorName === 'NotAuthorizedException' && /disabled/i.test(message)) {
      return 'AccountSuspended';
    }

    const errorMap: Record<string, string> = {
      UserNotFoundException: 'UserNotFoundException',
      NotAuthorizedException: 'NotAuthorizedException',
      UsernameExistsException: 'UsernameExistsException',
      InvalidPasswordException: 'InvalidPasswordException',
      CodeMismatchException: 'CodeMismatchException',
      // Cognito's name for a wrong code while confirming a new authenticator.
      EnableSoftwareTokenMFAException: 'CodeMismatchException',
      ExpiredCodeException: 'ExpiredCodeException',
      LimitExceededException: 'LimitExceededException',
      InvalidParameterException: 'InvalidParameterException',
      UserNotConfirmedException: 'UserNotConfirmedException',
      AliasExistsException: 'AliasExistsException',
    };

    return (errorMap[cognitoErrorName] || 'UnknownError') as AuthError['code'];
  }

  /**
   * Get user-friendly error messages
   */
  private getUserFriendlyMessage(code: AuthError['code'], originalMessage?: string): string {
    const messages: Record<AuthError['code'], string> = {
      UserNotFoundException: 'No account found with this email address.',
      NotAuthorizedException: 'Incorrect email or password.',
      UsernameExistsException: 'An account with this email already exists.',
      // The pool's real rules (06-api PasswordPolicy): no symbol is required (#594).
      InvalidPasswordException:
        'Password must be at least 8 characters, with an uppercase letter, a lowercase letter and a number.',
      CodeMismatchException: 'Invalid verification code. Please try again.',
      ExpiredCodeException: 'Verification code has expired. Please request a new one.',
      LimitExceededException: 'Too many attempts. Please try again later.',
      InvalidParameterException: 'Invalid input. Please check your information.',
      UserNotConfirmedException: 'Please verify your email address before signing in.',
      // #222 — owner decision 2026-10-13: no temporary addresses, said plainly
      // and with the reason. AWS also flags mistyped domains, hence "or mistyped".
      EmailThrowaway:
        "That looks like a temporary or mistyped email address. We'll need to reach you about your report long after today, so please use a permanent address you'll keep.",
      EmailUnreachable:
        "We can't send email to that address. Please check the spelling, or use a different address.",
      AliasExistsException: 'Another account already uses that email address.',
      AccountSuspended: 'Your account is paused. Please contact support@socialyse.ai.',
      ServiceError: 'Something went wrong on our side. Please try again in a moment.',
      NetworkError: 'Network error. Please check your connection.',
      UnknownError: originalMessage || 'An unexpected error occurred. Please try again.',
    };

    return messages[code];
  }
}

/**
 * Singleton instance of Cognito auth service
 */
export const cognitoAuthService = new CognitoAuthService();

/**
 * Default export for convenience
 */
export default cognitoAuthService;
